import React, { type PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVertical, Settings2, Trash2 } from 'lucide-react'
import { Button, EmptyState, Input, Label } from '@asad/crane-ui'

import {
  DASHBOARD_CANVAS_MIN_HEIGHT,
  DASHBOARD_CARD_ROW_HEIGHT,
  DASHBOARD_CARD_WIDTH,
  normalizePositions,
  type DashboardCardPosition,
  type DashboardViewDraft,
} from '../model/dashboardViews'
import type { DeviceStatusType, MapDevice } from '../types'
import { DevicePickerModal } from './DevicePickerModal'
import { FleetDeviceCard } from './FleetDeviceCard'

type DragState = {
  deviceId: string
  pointerId: number
  startPointerX: number
  startPointerY: number
  startX: number
  startY: number
  x: number
  y: number
}

type MovableCardProps = {
  device: MapDevice
  status?: DeviceStatusType
  position: DashboardCardPosition
  cardWidth: number
  dragging: boolean
  onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLButtonElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLButtonElement>) => void
  onKeyboardMove: (x: number, y: number) => void
  onRemove: () => void
}

const MovableCard = ({
  device,
  status,
  position,
  cardWidth,
  dragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onKeyboardMove,
  onRemove,
}: MovableCardProps) => {
  const { t } = useTranslation()

  return (
    <div
      className={dragging ? 'absolute z-10 min-w-0 ring-2 ring-crane-primary' : 'absolute min-w-0'}
      style={{ left: position.x, top: position.y, width: cardWidth }}
    >
      <div className='absolute right-2 top-2 z-20 flex gap-1'>
        <Button
          type='button'
          variant='secondary'
          size='icon'
          className={dragging ? 'size-8 cursor-grabbing touch-none' : 'size-8 cursor-grab touch-none'}
          aria-label={t('dashboardViews.moveCard', { name: device.name })}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 1 : 10
            if (event.key === 'ArrowLeft') onKeyboardMove(Math.max(0, position.x - step), position.y)
            else if (event.key === 'ArrowRight') onKeyboardMove(position.x + step, position.y)
            else if (event.key === 'ArrowUp') onKeyboardMove(position.x, Math.max(0, position.y - step))
            else if (event.key === 'ArrowDown') onKeyboardMove(position.x, position.y + step)
            else return
            event.preventDefault()
          }}
        >
          <GripVertical className='size-4' aria-hidden />
        </Button>
        <Button
          type='button'
          variant='destructive'
          size='icon'
          className='size-8'
          aria-label={t('dashboardViews.removeCard', { name: device.name })}
          onClick={onRemove}
        >
          <Trash2 className='size-4' aria-hidden />
        </Button>
      </div>
      <FleetDeviceCard device={device} deviceStatus={status} compact interactive={false} />
    </div>
  )
}

type DashboardViewEditorProps = {
  draft: DashboardViewDraft
  devices: MapDevice[]
  statuses: DeviceStatusType[]
  saveDisabled: boolean
  /** Zapis widoku w toku — przycisk pokazuje spinner i nie przyjmuje kliknięć. */
  saving?: boolean
  onChange: (draft: DashboardViewDraft) => void
  onCancel: () => void
  onSave: () => void
}

export const DashboardViewEditor = ({
  draft,
  devices,
  statuses,
  saveDisabled,
  saving = false,
  onChange,
  onCancel,
  onSave,
}: DashboardViewEditorProps) => {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [drag, setDrag] = React.useState<DragState | null>(null)
  const [announcement, setAnnouncement] = React.useState('')
  const [canvasWidth, setCanvasWidth] = React.useState<number | null>(null)
  const canvasRef = React.useRef<HTMLDivElement | null>(null)
  const statusById = React.useMemo(() => new Map(statuses.map((status) => [status.id, status])), [statuses])
  const devicesById = React.useMemo(() => new Map(devices.map((device) => [device.id, device])), [devices])
  const positions = normalizePositions(draft.deviceIds, draft.positions)
  const nameMissing = !draft.name.trim()
  const cardWidth = Math.min(DASHBOARD_CARD_WIDTH, canvasWidth ?? DASHBOARD_CARD_WIDTH)
  const clampX = (x: number) =>
    Math.min(Math.max(0, x), canvasWidth === null ? Number.POSITIVE_INFINITY : Math.max(0, canvasWidth - cardWidth))

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const updateWidth = () => setCanvasWidth(canvas.getBoundingClientRect().width)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [])

  const updatePosition = (deviceId: string, x: number, y: number) => {
    onChange({
      ...draft,
      positions: positions.map((position) =>
        position.deviceId === deviceId ? { ...position, x: clampX(x), y: Math.max(0, y) } : position,
      ),
    })
  }

  const displayedPositions = positions.map((position) => {
    const displayed = drag?.deviceId === position.deviceId ? { ...position, x: drag.x, y: drag.y } : position
    return { ...displayed, x: clampX(displayed.x) }
  })
  const canvasHeight = Math.max(
    DASHBOARD_CANVAS_MIN_HEIGHT,
    ...displayedPositions.map(({ y }) => y + DASHBOARD_CARD_ROW_HEIGHT),
  )

  const startDrag = (position: DashboardCardPosition, event: ReactPointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    setDrag({
      deviceId: position.deviceId,
      pointerId: event.pointerId,
      startPointerX: event.clientX,
      startPointerY: event.clientY,
      startX: position.x,
      startY: position.y,
      x: position.x,
      y: position.y,
    })
  }

  const moveDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    setDrag((current) => {
      if (!current || current.pointerId !== event.pointerId) return current
      return {
        ...current,
        x: clampX(current.startX + event.clientX - current.startPointerX),
        y: Math.max(0, current.startY + event.clientY - current.startPointerY),
      }
    })
  }

  const finishDrag = (event: ReactPointerEvent<HTMLButtonElement>, commit: boolean) => {
    if (!drag || drag.pointerId !== event.pointerId) return
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (commit) {
      updatePosition(drag.deviceId, drag.x, drag.y)
      setAnnouncement(t('dashboardViews.cardMoved', { name: devicesById.get(drag.deviceId)?.name }))
    }
    setDrag(null)
  }

  return (
    <div className='flex min-h-crane-dashboard-viewport flex-col gap-3.5 p-4'>
      <div className='flex flex-col gap-2.5 rounded-crane-md bg-crane-card-inset p-3.5 lg:flex-row lg:items-end'>
        <div className='min-w-0 flex-1'>
          {/* Komunikat w linii etykiety — pod polem wydłużałby kolumnę i przyciski zjeżdżałyby poniżej pola. */}
          <div className='mb-1.5 flex items-baseline justify-between gap-3'>
            <Label htmlFor='dashboard-view-name' className='block text-xs font-semibold'>
              {t('dashboardViews.viewName')}
            </Label>
            {nameMissing && (
              <p id='dashboard-view-name-error' className='text-xs font-medium text-crane-destructive'>
                {t('dashboardViews.nameRequired')}
              </p>
            )}
          </div>
          <Input
            id='dashboard-view-name'
            className='h-9 bg-crane-card'
            value={draft.name}
            maxLength={100}
            aria-invalid={nameMissing}
            aria-describedby={nameMissing ? 'dashboard-view-name-error' : undefined}
            onChange={(event) => onChange({ ...draft, name: event.target.value })}
          />
        </div>
        <Button variant='outline' className='gap-2' onClick={() => setPickerOpen(true)}>
          <Settings2 className='size-4' aria-hidden />
          {t('dashboardViews.manageDevices')}
        </Button>
        <Button variant='ghost' onClick={onCancel} disabled={saving}>
          {t('commonActions.cancel')}
        </Button>
        <Button disabled={saveDisabled} loading={saving} loadingLabel={t('commonActions.saving')} onClick={onSave}>
          {t('dashboardViews.saveView')}
        </Button>
      </div>

      <div className='min-h-80 flex-1 overflow-hidden rounded-crane-md bg-crane-card-inset p-3.5'>
        {positions.length ? (
          <div ref={canvasRef} className='relative min-h-full' style={{ height: canvasHeight }}>
            {drag && (
              <div
                className='pointer-events-none absolute rounded-crane-md border border-dashed border-crane-primary/50 bg-crane-primary/5'
                style={{
                  left: clampX(drag.startX),
                  top: drag.startY,
                  width: cardWidth,
                  height: DASHBOARD_CARD_ROW_HEIGHT,
                }}
                aria-hidden
              />
            )}
            {displayedPositions.map((position) => {
              const device = devicesById.get(position.deviceId)
              return device ? (
                <MovableCard
                  key={position.deviceId}
                  device={device}
                  status={statusById.get(position.deviceId)}
                  position={position}
                  cardWidth={cardWidth}
                  dragging={drag?.deviceId === position.deviceId}
                  onPointerDown={(event) => startDrag(position, event)}
                  onPointerMove={moveDrag}
                  onPointerUp={(event) => finishDrag(event, true)}
                  onPointerCancel={(event) => finishDrag(event, false)}
                  onKeyboardMove={(x, y) => {
                    updatePosition(position.deviceId, x, y)
                    setAnnouncement(t('dashboardViews.cardMoved', { name: device.name }))
                  }}
                  onRemove={() => {
                    const ids = draft.deviceIds.filter((id) => id !== position.deviceId)
                    onChange({
                      ...draft,
                      deviceIds: ids,
                      positions: normalizePositions(ids, positions),
                    })
                    setAnnouncement(t('dashboardViews.cardRemoved', { name: device.name }))
                  }}
                />
              ) : null
            })}
          </div>
        ) : (
          <EmptyState title={t('dashboardViews.emptyDraft')} hint={t('dashboardViews.emptyDraftHint')} />
        )}
      </div>
      <div className='sr-only' aria-live='polite'>
        {announcement}
      </div>
      <DevicePickerModal
        open={pickerOpen}
        devices={devices}
        selectedIds={draft.deviceIds}
        onCancel={() => setPickerOpen(false)}
        onApply={(ids) => {
          onChange({ ...draft, deviceIds: ids, positions: normalizePositions(ids, positions) })
          setPickerOpen(false)
        }}
      />
    </div>
  )
}
